import { useMemo, useEffect, useCallback } from "react"
import { useDispatch } from "react-redux"
import PropTypes from "prop-types"
import { size, isEmpty } from "lodash"
import AddTrade from "connected-components/add-trade"
import { Creators as DialogCreators } from "modules/dialog/model"
import AISummarizer from "modules/ai-summary"
import { ACTIONS } from "modules/ai-summary/constants"
import SaveExperiment from "modules/save-experiment"
import ResetButton from "modules/rebalance/components/reset-button"
import { computeAnalyticsOutput } from "modules/rebalance/helpers/queries/helpers"
import SaveExperimentButton from "components/save-experiment-button"
import { useRebalancer, useSimulateActions, computeSimulationExperiment, usePortfolioHoldings } from "hooks/rebalancer"
import { useIsAiTermsAccepted, useAccountAlias } from "resources/account"
import { useModelMetadata } from "resources/models"
import { RECENT_INSTRUMENT_SEARCH_TYPES } from "resources/instruments"
import { computeAttributionMethod } from "helpers/risk"
import { computeModeFilteredSegment } from "helpers/segments"
import { useIntradayState } from "connected-components/dashboard/hooks/use-intraday-state"
import { useClassifications } from "resources/classifications"
import { LOADING, OK } from "helpers/data-statuses"
import { useAnalytics } from "connected-components/analytics"
import { useAppSelector } from "hooks/redux"
import { useDashboardId } from "../../context/dashboard-id"
import { DEFAULT_CORE_STATE } from "../../store/core-slice"
import { DEFAULT_TRADE_STATE } from "../../store/trade-slice"
import { useCoreActions } from "../../hooks/use-core-actions"
import { useTradeActions } from "../../hooks/use-trade-actions"
import { DASHBOARD_EVENTS } from "../../analytics"
import { applyDashboardSavedExperimentFollowUp } from "./apply-dashboard-saved-experiment-follow-up"
import { setSaveExperimentRegistration } from "../../save-experiment-launcher-store"

export default function TraderManager({ instrumentsWithMetadata }) {
  const dispatch = useDispatch()
  const dashboardId = useDashboardId()

  const coreState = useAppSelector((s) => s.dashboard.core[dashboardId] ?? DEFAULT_CORE_STATE)
  const modelId = useAppSelector((s) => s.dashboard.model[dashboardId]?.modelId ?? null)
  const tradeState = useAppSelector((s) => s.dashboard.trade[dashboardId] ?? DEFAULT_TRADE_STATE)

  const {
    endDate,
    instrument,
    base,
    filter,
    normalization,
    comparisonInstrument,
    intradayBatchMetadata,
    startDate,
    period,
    isIntraday,
    selectedMode,
  } = coreState

  const {
    trades,
    actionsBase,
    isExpanded: expand,
  } = tradeState

  const { setSecondInstrument, setComparisonInstrument, setIsOptimizePanelExpanded, setOptimizationSourceKind, setOptimizationExperimentSource } = useCoreActions()
  const { resetTrades, updateTradeFilters } = useTradeActions()

  const analytics = useAnalytics()

  const isActive = !!base
  const hasFilters = !isEmpty(filter)

  const attributionMethod = useMemo(() => computeAttributionMethod(isActive), [isActive])
  const [portfolioHoldings, portfolioHoldingsStatus] = usePortfolioHoldings(instrument, modelId, endDate)
  const [secondInstrument, secondInstrumentStatus] = useRebalancer(trades, instrument, modelId, endDate, base, intradayBatchMetadata?.id)
  const [modelMetadata] = useModelMetadata(modelId)
  const simulateActions = useSimulateActions(trades)
  const isAiTermsAccepted = useIsAiTermsAccepted()
  const account = useAccountAlias()
  const [{ isIntradayMode }] = useIntradayState()
  const analyticsSegment = useMemo(() => computeModeFilteredSegment(filter, normalization, isIntradayMode), [normalization, filter, isIntradayMode])
  const aiSummarizerBase = useMemo(() => (base ? { id: base.id, type: base.type, weight: base.weight } : null), [base])
  const { analyticsOutput } = useMemo(() => computeAnalyticsOutput({ isActive, base: aiSummarizerBase, analyticsSegment, expand }), [isActive, aiSummarizerBase, analyticsSegment, expand])
  const [classifications, classificationsStatus] = useClassifications()

  useEffect(() => {
    if (secondInstrumentStatus === LOADING && trades.length > 0) {
      return
    }

    const overrideMode = trades.length > 1 ? selectedMode : null
    setSecondInstrument(secondInstrument, secondInstrumentStatus, overrideMode)
  }, [secondInstrument, secondInstrumentStatus, setSecondInstrument, selectedMode, trades])

  useEffect(() => {
    updateTradeFilters(filter)
  }, [filter, updateTradeFilters])

  const isIntradayWithBatch = isIntraday && intradayBatchMetadata?.id

  const handleResetRebalance = () => {
    const nbTrades = size(trades)
    dispatch(DialogCreators.open({
      title: "Reset Trades",
      contentText: `Are you sure you want to reset your ${nbTrades === 1 ? "trade" : `${nbTrades} trades`} ?`,
      buttons: [{
        text: "Reset trades",
        type: "danger",
        action: () => {
          analytics.track(DASHBOARD_EVENTS.TRADES_CLEARED, { numberOfTrades: nbTrades })
          resetTrades()
        },
      }],
    }))
  }

  const computedExperiment = useMemo(() => {
    let experimentDate = secondInstrument?.dates[0]

    if (isEmpty(experimentDate) && comparisonInstrument?.isOptimization && comparisonInstrument?.dates?.[0]) {
      experimentDate = comparisonInstrument.dates[0]
    }

    if (isEmpty(experimentDate) && portfolioHoldingsStatus === OK) {
      experimentDate = portfolioHoldings
    }

    const simulationPayload = computeSimulationExperiment(experimentDate, actionsBase, simulateActions, null, endDate)
    if (modelId && simulationPayload) {
      return { ...simulationPayload, modelId }
    }
    return simulationPayload
  },
  [simulateActions, actionsBase, endDate, secondInstrument, comparisonInstrument, portfolioHoldings, portfolioHoldingsStatus, modelId])

  const handleSavedExperiment = useCallback((newExperiment, applyTo = {}) => {
    applyDashboardSavedExperimentFollowUp(newExperiment, applyTo, {
      instrument,
      setComparisonInstrument,
      setIsOptimizePanelExpanded,
      setOptimizationExperimentSource,
      setOptimizationSourceKind,
    })
  }, [
    instrument,
    setComparisonInstrument,
    setIsOptimizePanelExpanded,
    setOptimizationExperimentSource,
    setOptimizationSourceKind,
  ])

  const toggleSaveExperiment = useCallback(() => {
    const isOneDayExperiment = endDate === modelMetadata?.availability.currentDate
    dispatch(DialogCreators.open({
      title: "Save Experiment",
      content: <SaveExperiment
        experiment={computedExperiment}
        portfolioId={instrumentsWithMetadata?.original.id}
        portfolioAlias={instrumentsWithMetadata?.original.alias}
        isOneDayExperiment={isOneDayExperiment}
        onSuccess={handleSavedExperiment}
        showPostSaveApplyOptions
        existingComparisonInstrument={comparisonInstrument}
        searchType={RECENT_INSTRUMENT_SEARCH_TYPES.DASHBOARD}
      />,
    }))
  }, [
    dispatch,
    computedExperiment,
    instrumentsWithMetadata,
    modelMetadata,
    endDate,
    comparisonInstrument,
    handleSavedExperiment,
  ])

  const canSaveExperiment = useMemo(() =>
    instrumentsWithMetadata?.original.type === "PORTFOLIO" && !isEmpty(computedExperiment) && !isIntradayWithBatch,
  [instrumentsWithMetadata, computedExperiment, isIntradayWithBatch])

  const tooltipOverride = useMemo(() => {
    if (instrumentsWithMetadata?.original.type !== "PORTFOLIO") {
      return "Can only save experiments on portfolios"
    }

    if (isIntradayWithBatch) {
      return "Can't save experiments in intraday mode"
    }

    return undefined
  }, [instrumentsWithMetadata, isIntradayWithBatch])

  useEffect(() => {
    setSaveExperimentRegistration({
      open: toggleSaveExperiment,
      disabled: !canSaveExperiment,
      tooltip: tooltipOverride,
    })
    return () => setSaveExperimentRegistration(null)
  }, [toggleSaveExperiment, canSaveExperiment, tooltipOverride])

  const Summarizer = isAiTermsAccepted && instrument?.type === "PORTFOLIO"
    ? <div className="dashboard-ai-summarizer">
      <AISummarizer
        key="aiSummarizer"
        isDashboard={true}
        {...{
          noSpinner: true,
          tooltip: "Open Simulator Summary",
          action: ACTIONS.SIMULATOR_SUMMARY,
          account,
          modelId,
          resourceId: instrument.id,
          resourceType: "PORTFOLIO",
          ...(isActive && { base: aiSummarizerBase }),
          period,
          startDate,
          endDate,
          extraParameters: {
            attributionMethod,
            actions: simulateActions,
            actionsBase,
            batchId: null,
            analyticsOutput,
          },
        }}
      />
    </div>
    : null

  return (
    <div style={{ display: "flex" }}>
      {/* Classications changing from LOADING to OK or ERROR will trigger a re-render of the AddTrade component which will
      trigger a re-fetch of all the queries. OK and ERROR are both ok as they are stable states. */}
      {classificationsStatus !== LOADING && <AddTrade classifications={classifications} currentGrouping={tradeState.assetViewGroupBy} hasFilters={hasFilters} />}
      {Summarizer}
      <ResetButton disabled={!trades.length} resetRebalance={handleResetRebalance} />
      <SaveExperimentButton {...{ toggleSaveExperiment, saveDisabled: !canSaveExperiment, tooltipOverride }} />
    </div>
  )
}

TraderManager.propTypes = {
  instrumentsWithMetadata: PropTypes.shape({
    original: PropTypes.object,
    rebalanced: PropTypes.object,
    comparison: PropTypes.object,
    base: PropTypes.object,
  }).isRequired,
}
